import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { financeDashboardService, reminderService, productService, batchService } from '../../services/financeService';
import { getUserData } from '../../utils/authUtils';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  ErrorOutline as OverdueIcon,
  EditNote as DraftIcon,
  NotificationsActive as ReminderIcon,
  Inventory2 as StockIcon,
  CheckCircleOutline as OkIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Remove as FlatIcon,
  WarningAmber as LowIcon,
} from '@mui/icons-material';
import './Financial.css';
import './Dashboard.css';

// ── formatting ───────────────────────────────────────────────────────────────
const n = (v) => Number(v) || 0;
const money = (v) => {
  const x = n(v);
  return (x < 0 ? '-' : '') + Math.abs(x).toLocaleString('en-US', { maximumFractionDigits: 0 });
};
const pkr = (v) => `PKR ${money(v)}`;
const kgFmt = (v) => `${Math.round(n(v)).toLocaleString('en-US')} kg`;
// Axis labels: 1.2M, 350K — full numbers stay in the tooltips and cards
const compact = (v) => {
  const a = Math.abs(n(v));
  if (a >= 1e6) return `${(n(v) / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e3) return `${Math.round(n(v) / 1e3)}K`;
  return String(Math.round(n(v)));
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

// ── small pieces ─────────────────────────────────────────────────────────────
const Delta = ({ now, before }) => {
  const a = n(now), b = n(before);
  if (a === 0 && b === 0) return null;
  if (b === 0) return <span className="dash-delta up"><UpIcon sx={{ fontSize: 12 }} aria-hidden="true" />new this month</span>;
  const pct = ((a - b) / Math.abs(b)) * 100;
  const cls = Math.abs(pct) < 0.5 ? 'flat' : pct > 0 ? 'up' : 'down';
  const Icon = cls === 'flat' ? FlatIcon : pct > 0 ? UpIcon : DownIcon;
  return (
    <span className={`dash-delta ${cls}`} title="Compared with last month">
      <Icon sx={{ fontSize: 12 }} aria-hidden="true" />{Math.abs(pct).toFixed(0)}% vs last month
    </span>
  );
};

// A 6-month trend line with the latest point marked
const Spark = ({ values, color }) => {
  const v = (values || []).map(n);
  if (v.length < 2 || Math.max(...v) <= 0) return <span style={{ width: 84 }} />;
  const w = 84, h = 28, max = Math.max(...v);
  const pts = v.map((y, i) => [(i / (v.length - 1)) * (w - 6) + 3, h - 4 - (y / max) * (h - 8)]);
  const last = pts[pts.length - 1];
  return (
    <svg className="dash-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Last six months trend">
      <polyline points={pts.map(p => p.join(',')).join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill={color} stroke="var(--color-bg-primary)" strokeWidth="2" />
    </svg>
  );
};

const Kpi = ({ label, children, sub, foot, className = '' }) => (
  <div className={`dash-kpi ${className}`}>
    <span className="dash-kpi-label">{label}</span>
    <div className="dash-kpi-value"><span className="dash-kpi-unit">PKR</span>{children}</div>
    {sub && <span className="dash-kpi-sub">{sub}</span>}
    <div className="dash-kpi-foot">{foot}</div>
  </div>
);

// ── grouped bars: sales vs money spent, last 6 months ───────────────────────
const SalesVsSpent = ({ revenue = [], expenses = [] }) => {
  const [hover, setHover] = useState(null);
  const W = 640, H = 330, L = 46, R = 8, T = 14, B = 30;
  const months = revenue.map((m, i) => ({ label: m.month, sales: n(m.amount), spent: n(expenses[i]?.amount) }));
  const top = Math.max(1, ...months.flatMap(m => [m.sales, m.spent]));
  const step = (() => { const raw = top / 4; const mag = 10 ** Math.floor(Math.log10(raw)); return Math.ceil(raw / mag) * mag; })();
  const yMax = step * 4;
  const y = (v) => T + (H - T - B) * (1 - v / yMax);
  const colW = (W - L - R) / Math.max(months.length, 1);
  const barW = Math.min(26, colW / 3);

  if (!months.length || top <= 1) {
    return <div className="dash-empty">No sales or spending in the last six months yet.</div>;
  }
  const h = hover != null ? months[hover] : null;
  const cx = hover != null ? L + colW * hover + colW / 2 : 0;
  return (
    <div className="dash-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label={`Sales and money spent for the last ${months.length} months. ` + months.map(m => `${m.label}: sales ${money(m.sales)}, spent ${money(m.spent)}`).join('; ')}>
        {[0, 1, 2, 3, 4].map(i => (
          <g key={i}>
            <line className="grid" x1={L} x2={W - R} y1={y(step * i)} y2={y(step * i)} />
            <text className="axis-text" x={L - 8} y={y(step * i) + 4} textAnchor="end">{compact(step * i)}</text>
          </g>
        ))}
        {months.map((m, i) => {
          const x0 = L + colW * i + colW / 2;
          const bar = (val, dx, fill) => {
            const top0 = y(val);
            const hgt = Math.max(H - B - top0, val > 0 ? 2 : 0);
            return <rect className="bar" x={x0 + dx} y={H - B - hgt} width={barW} height={hgt} rx="4" fill={fill}
              opacity={hover == null || hover === i ? 1 : 0.4} />;
          };
          return (
            <g key={i} className="col" tabIndex={0}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)} onBlur={() => setHover(null)}>
              <rect className="hit" x={L + colW * i} y={T} width={colW} height={H - T - B} fill="transparent" />
              {bar(m.sales, -barW - 1, 'var(--dash-in)')}
              {bar(m.spent, 1, 'var(--dash-out)')}
              <text className="axis-text" x={x0} y={H - 9} textAnchor="middle">{m.label}</text>
            </g>
          );
        })}
      </svg>
      {h && (
        <div className="dash-tip" style={{ left: `${(cx / W) * 100}%`, top: `${(y(Math.max(h.sales, h.spent)) / H) * 100}%` }} role="status">
          <strong>{h.label}</strong>
          <div className="dash-tip-row"><span><i className="dot" style={{ background: 'var(--dash-in)', outline: '1px solid #fff5' }} />Sales</span><span>{pkr(h.sales)}</span></div>
          <div className="dash-tip-row"><span><i className="dot" style={{ background: 'var(--dash-out)' }} />Spent</span><span>{pkr(h.spent)}</span></div>
        </div>
      )}
      <table className="sr-only">
        <caption>Sales and money spent by month</caption>
        <thead><tr><th>Month</th><th>Sales</th><th>Money spent</th></tr></thead>
        <tbody>{months.map(m => <tr key={m.label}><td>{m.label}</td><td>{money(m.sales)}</td><td>{money(m.spent)}</td></tr>)}</tbody>
      </table>
    </div>
  );
};

const Skeleton = () => (
  <div className="dash" aria-busy="true" aria-label="Loading dashboard">
    <div className="dash-skel" style={{ height: 56, width: '40%' }} />
    <div className="dash-attn">{[0, 1, 2, 3].map(i => <div key={i} className="dash-skel" style={{ height: 64 }} />)}</div>
    <div className="dash-kpis">{[0, 1, 2, 3, 4].map(i => <div key={i} className="dash-skel" style={{ height: 118 }} />)}</div>
    <div className="dash-skel" style={{ height: 300 }} />
  </div>
);

// ── page ─────────────────────────────────────────────────────────────────────
const FinancialDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dash, setDash] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    const [d, r, p, b] = await Promise.allSettled([
      financeDashboardService.getDashboard(),
      reminderService.getAll(),
      productService.getAll({ activeOnly: true }),
      batchService.getAll(),
    ]);
    if (d.status === 'fulfilled' && d.value?.success) setDash(d.value.data);
    else setError(d.reason?.message || d.value?.message || 'Could not load the dashboard');
    if (r.status === 'fulfilled') setReminders(r.value);
    if (p.status === 'fulfilled') { const l = p.value?.data || p.value || []; setProducts(Array.isArray(l) ? l : []); }
    if (b.status === 'fulfilled') setBatches(b.value?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const user = getUserData();
  const lowStock = useMemo(() => products.filter(p =>
    p.reorderLevel != null && p.currentStock != null && n(p.currentStock) <= n(p.reorderLevel)), [products]);
  const openBatches = useMemo(() => batches.filter(b => b.status === 'OPEN' || b.status === 'PROCESSING').slice(0, 6), [batches]);
  const stock = useMemo(() => products
    .filter(p => p.category !== 'CONSUMABLE' && n(p.currentStock) > 0)
    .sort((a, b) => n(b.currentStock) - n(a.currentStock)).slice(0, 6), [products]);
  const spendRows = useMemo(() => {
    const entries = Object.entries(dash?.expensesByCategory || {}).map(([k, v]) => [k, n(v)]).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
    const head = entries.slice(0, 6);
    const rest = entries.slice(6).reduce((s, [, v]) => s + v, 0);
    return rest > 0 ? [...head, ['Everything else', rest]] : head;
  }, [dash]);

  if (loading) return <Skeleton />;
  if (error || !dash) {
    return (
      <div className="dash">
        <div className="dash-card" role="alert">
          <h3 className="dash-card-title">The dashboard could not load</h3>
          <p className="dash-sub">{error || 'No data came back.'} Check your connection and try again.</p>
          <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => { setLoading(true); load(); }}>Try again</button>
        </div>
      </div>
    );
  }

  const revSeries = (dash.monthlyRevenue || []).map(m => m.amount);
  const spendSeries = (dash.monthlyExpenses || []).map(m => m.amount);
  const profit = n(dash.netProfit);
  const owedIn = n(dash.totalOutstanding), owedOut = n(dash.totalPayables);
  const owedTotal = owedIn + owedOut;
  const attention = [
    { key: 'overdue', Icon: OverdueIcon, tone: 'bad', count: n(dash.overdueInvoices), label: 'Overdue invoices', path: '/invoices' },
    { key: 'draft', Icon: DraftIcon, tone: 'info', count: n(dash.draftInvoices), label: 'Invoices not sent yet', path: '/invoices' },
    { key: 'rem', Icon: ReminderIcon, tone: 'warn', count: n(reminders?.overdueCount) + n(reminders?.dueTodayCount), label: 'Payments due now', path: '/reminders' },
    { key: 'low', Icon: StockIcon, tone: 'warn', count: lowStock.length, label: 'Items running low', path: '/products' },
  ];
  const allClear = attention.every(a => a.count === 0);
  const orgName = user?.organization?.name || user?.organizationName || '';

  return (
    <div className="dash">
      {/* Header */}
      <header className="dash-head">
        <div>
          <p className="dash-eyebrow">{orgName ? `${orgName} · ` : ''}{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <h2 className="dash-title">{greeting()}</h2>
          <p className="dash-sub">{allClear ? 'Nothing needs your attention right now.' : 'Here is what needs you today, and how the business is doing.'}</p>
        </div>
        <div className="dash-actions">
          <button className="btn-secondary" onClick={() => navigate('/batches')}><AddIcon sx={{ fontSize: 18 }} aria-hidden="true" />New batch</button>
          <button className="btn-secondary" onClick={() => navigate('/gate-passes')}><AddIcon sx={{ fontSize: 18 }} aria-hidden="true" />New gate pass</button>
          <button className="btn-primary" onClick={() => navigate('/invoices')}><AddIcon sx={{ fontSize: 18 }} aria-hidden="true" />New invoice</button>
          <button className="btn-secondary" onClick={() => { setLoading(true); load(); }} aria-label="Refresh dashboard"><RefreshIcon sx={{ fontSize: 18 }} aria-hidden="true" /></button>
        </div>
      </header>

      {/* Needs attention */}
      <section aria-label="Needs your attention">
        <h3 className="dash-section-title">Needs your attention</h3>
        {allClear ? (
          <div className="dash-clear"><OkIcon sx={{ fontSize: 20 }} aria-hidden="true" />All clear: no overdue invoices, nothing waiting to be sent, no payments due and no low stock.</div>
        ) : (
          <div className="dash-attn">
            {attention.filter(a => a.count > 0).map(({ key, Icon, tone, count, label, path }) => (
              <button key={key} className={`dash-attn-item is-${tone}`} onClick={() => navigate(path)}>
                <span className="dash-attn-icon"><Icon sx={{ fontSize: 20 }} aria-hidden="true" /></span>
                <span>
                  <span className="dash-attn-count">{count.toLocaleString()}</span>
                  <span className="dash-attn-label">{label}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Headline numbers */}
      <section aria-label="Headline numbers">
        <h3 className="dash-section-title">How the business is doing · this year</h3>
        <div className="dash-kpis">
          <Kpi label="Sales" sub={`This month: ${pkr(dash.totalRevenueThisMonth)}`}
            foot={<><Delta now={dash.totalRevenueThisMonth} before={dash.totalRevenueLastMonth} /><Spark values={revSeries} color="var(--dash-in)" /></>}>
            {money(dash.totalRevenue)}
          </Kpi>
          <Kpi label="Money spent" sub={`This month: ${pkr(dash.totalExpensesThisMonth)}`}
            foot={<><Delta now={dash.totalExpensesThisMonth} before={dash.totalExpensesLastMonth} /><Spark values={spendSeries} color="var(--dash-out)" /></>}>
            {money(dash.totalExpenses)}
          </Kpi>
          <Kpi label={profit >= 0 ? 'Profit' : 'Loss'} className={`is-profit ${profit >= 0 ? 'is-pos' : 'is-neg'}`}
            sub={n(dash.totalRevenue) <= 0 ? 'Shows once you have made a sale'
              : profit >= 0 ? `${n(dash.profitMarginPercent).toFixed(1)}% of sales`
              : `Sales cover only ${(n(dash.totalRevenue) / Math.max(n(dash.totalExpenses), 1) * 100).toFixed(n(dash.totalRevenue) / Math.max(n(dash.totalExpenses), 1) < 0.1 ? 1 : 0)}% of the money spent`}
            foot={<span className="dash-kpi-sub">Sales minus money spent</span>}>
            {money(Math.abs(profit))}
          </Kpi>
          <Kpi label="Customers owe you" sub={`${n(dash.overdueInvoices)} overdue invoice${n(dash.overdueInvoices) === 1 ? '' : 's'}`}
            foot={<button className="dash-link" onClick={() => navigate('/credit')}>See who →</button>}>
            {money(owedIn)}
          </Kpi>
          <Kpi label="You owe vendors" sub="Bills not paid yet"
            foot={<button className="dash-link" onClick={() => navigate('/purchases')}>See bills →</button>}>
            {money(owedOut)}
          </Kpi>
        </div>
      </section>

      {/* Trend + who owes whom */}
      <div className="dash-row">
        <section className="dash-card" aria-label="Sales against money spent">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Sales and money spent</h3>
            <div className="dash-legend" aria-hidden="true">
              <span className="dash-legend-item"><i className="dash-swatch" style={{ background: 'var(--dash-in)' }} />Sales</span>
              <span className="dash-legend-item"><i className="dash-swatch" style={{ background: 'var(--dash-out)' }} />Money spent</span>
            </div>
          </div>
          <SalesVsSpent revenue={dash.monthlyRevenue} expenses={dash.monthlyExpenses} />
        </section>

        <section className="dash-card" aria-label="Who owes whom">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Who owes whom</h3>
            <button className="dash-link" onClick={() => navigate('/payments')}>Payments →</button>
          </div>
          {owedTotal > 0 ? (
            <div className="dash-balance-bar" role="img" aria-label={`Customers owe you ${money(owedIn)}, you owe vendors ${money(owedOut)}`}>
              <span style={{ width: `${(owedIn / owedTotal) * 100}%`, background: 'var(--dash-in)' }} />
              <span style={{ width: `${(owedOut / owedTotal) * 100}%`, background: 'var(--dash-out)' }} />
            </div>
          ) : <div className="dash-empty">Nothing owed either way.</div>}
          <div className="dash-balance-rows">
            <div className="dash-balance-row"><span className="name"><i className="dash-swatch" style={{ background: 'var(--dash-in)' }} />Customers owe you</span><span className="num">{pkr(owedIn)}</span></div>
            <div className="dash-balance-row"><span className="name"><i className="dash-swatch" style={{ background: 'var(--dash-out)' }} />You owe vendors</span><span className="num">{pkr(owedOut)}</span></div>
            <div className="dash-balance-row"><span className="name">Cash received this year</span><span className="num">{pkr(dash.totalReceived)}</span></div>
            <div className="dash-balance-row"><span className="name">Cash paid out this year</span><span className="num">{pkr(dash.totalPaid)}</span></div>
          </div>
          <div className={`dash-net ${owedIn - owedOut >= 0 ? 'pos' : 'neg'}`}>
            <span>{owedIn - owedOut >= 0 ? 'Once everyone pays, you are ahead by' : 'Once everyone pays, you are short by'}</span>
            <strong>{pkr(Math.abs(owedIn - owedOut))}</strong>
          </div>
          <div className="dash-inv">
            <h4 className="dash-inv-title">Your invoices</h4>
            <div className="dash-inv-grid">
              <div className="dash-inv-cell"><b>{n(dash.draftInvoices)}</b><span>Not sent</span></div>
              <div className="dash-inv-cell"><b>{n(dash.sentInvoices)}</b><span>Waiting for payment</span></div>
              <div className="dash-inv-cell"><b>{n(dash.paidInvoices)}</b><span>Paid</span></div>
              <div className={`dash-inv-cell ${n(dash.overdueInvoices) > 0 ? 'bad' : ''}`}><b>{n(dash.overdueInvoices)}</b><span>Overdue</span></div>
            </div>
          </div>
        </section>
      </div>

      {/* Batches at work */}
      <section className="dash-card" aria-label="Batches at work">
        <div className="dash-card-head">
          <h3 className="dash-card-title">Batches at work</h3>
          <button className="dash-link" onClick={() => navigate('/batches')}>All batches →</button>
        </div>
        {openBatches.length === 0 ? (
          <div className="dash-empty">
            No open batches. A batch tracks one lot of goods from buying to selling.
            <div style={{ marginTop: 12 }}><button className="btn-primary" onClick={() => navigate('/batches')}>Start a batch</button></div>
          </div>
        ) : (
          <div className="dash-batches">
            {openBatches.map(b => {
              const kin = n(b.entryWeightKg), kout = n(b.exitWeightKg);
              const pct = kin > 0 ? Math.min(100, (kout / kin) * 100) : 0;
              return (
                <button key={b.id} className={`dash-batch ${kin === 0 ? 'is-empty' : ''}`} onClick={() => navigate('/batches')} aria-label={`Open batch ${b.batchCode}`}>
                  <span>
                    <span className="dash-batch-code">{b.batchCode}</span>
                    <span className="dash-batch-meta">{b.productName}{b.godownName ? ` · ${b.godownName}` : ''}</span>
                  </span>
                  <span className="dash-progress-wrap">
                    {kin === 0 ? <span className="dash-empty-line">No goods received yet</span> : (
                      <>
                        <div className="dash-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label="Share of goods sent out">
                          <span style={{ width: `${pct}%` }} />
                        </div>
                        <div className="dash-progress-cap"><span>{kgFmt(kin)} received</span><span>{kgFmt(kout)} sent out</span></div>
                      </>
                    )}
                  </span>
                  <span className="dash-money"><span className="v">{pkr(b.totalCost)}</span><span className="l">spent</span></span>
                  <span className="dash-money earned"><span className="v">{pkr(b.totalRevenue)}</span><span className="l">earned</span></span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Spending + stock */}
      <div className="dash-row even">
        <section className="dash-card" aria-label="Where the money was spent">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Where the money was spent</h3>
            <span className="dash-card-note">this year</span>
          </div>
          {spendRows.length === 0 ? <div className="dash-empty">No spending recorded yet.</div> : (
            <div className="dash-rank">
              {spendRows.map(([name, val]) => {
                const share = (val / spendRows.reduce((s, [, v]) => s + v, 0)) * 100;
                return (
                  <div className="dash-rank-row" key={name}>
                    <span className="dash-rank-name" title={name}>{name}</span>
                    <span className="dash-rank-track"><span className="dash-rank-fill" style={{ display: 'block', width: `${share}%`, background: 'var(--dash-out)' }} /></span>
                    <span className="dash-rank-val">{pkr(val)}<small>{share > 0 && share < 1 ? '<1' : share.toFixed(0)}%</small></span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="dash-card" aria-label="Stock in hand">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Stock in hand</h3>
            <button className="dash-link" onClick={() => navigate('/products')}>Product catalog →</button>
          </div>
          {stock.length === 0 ? <div className="dash-empty">No stock recorded yet. It appears when a gate pass is received.</div> : (
            <div className="dash-rank">
              {stock.map(p => {
                const max = n(stock[0].currentStock);
                const low = p.reorderLevel != null && n(p.currentStock) <= n(p.reorderLevel);
                return (
                  <div className="dash-rank-row" key={p.id}>
                    <span className="dash-rank-name" title={p.name}>{p.name}{low && <span className="dash-flag"><LowIcon sx={{ fontSize: 13 }} aria-hidden="true" />Low</span>}</span>
                    <span className="dash-rank-track"><span className="dash-rank-fill" style={{ display: 'block', width: `${(n(p.currentStock) / max) * 100}%`, background: low ? 'var(--color-error)' : 'var(--dash-in)' }} /></span>
                    <span className="dash-rank-val">{Math.round(n(p.currentStock)).toLocaleString('en-US')}<small>{p.unit || ''}</small></span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default FinancialDashboard;
